import type { RouteConfig } from "@asteasolutions/zod-to-openapi";
import { defaultErrorResponses, problemResponse, registry } from "../registry.js";
import { z } from "../zod.js";
import {
  CustomerNoteCreateRequest,
  CustomerNoteListResponse,
  CustomerNoteWriteResponse,
  CustomerDuplicateCheckRequest,
  CustomerDuplicateCheckResponse,
  CustomerTagAttachRequest,
  CustomerTagsResponse,
  SalesAttachmentCreateRequest,
  SalesAttachmentDownloadResponse,
  SalesAttachmentListResponse,
  SalesAttachmentWriteResponse,
  SalesTag,
  SalesTagArchivedFilter,
  SalesTagCreateRequest,
  SalesTagListResponse,
  SalesTagUpdateRequest,
} from "./crm-schemas.js";
import {
  CustomerMergePreview,
  CustomerMergePreviewRequest,
  CustomerMergeRequest,
  CustomerMergeResult,
} from "./merge-schemas.js";
import { Customer } from "./schemas.js";

// Module 09, THE CUSTOMER RECORD's HTTP surface (spec 09 §5's P3-E2 PR E2-2
// rows 1 to 17 and 3a; D-323 rulings 1 to 12, D-324 rulings 1 to 16, D-335).
// Nineteen routes: the notes timeline, the append, pin, unpin and redact; the
// tag list, its create, rename and archive; a customer's tag and untag; since
// E2-2a2, the attach, the list, the download and the detach of a sales
// attachment; since E2-2c, the duplicate check; and since E2-2b, the merge
// preview, the merge and the restore. The amended rows (the customer create,
// PATCH, list and 360, the quote and order lists' `customer_tag_id`, the
// assignments route's `expected_owner_user_id`, resolve-identity's one hop
// and every `CUSTOMER_ARCHIVED` door's `merged_into_customer_id`) are
// registered where their routes already live (`./register.ts`,
// `./quote-register.ts`, `./opportunity-register.ts`). The Documents tab
// reads the customer's quotes and orders through the books' existing
// `customer_id` filter (`GET /v1/quotes`, `GET /v1/orders`) and adds no
// route of its own (spec 09 §4.2's E2-2a2 annotation).
//
// PERMISSIONS. One code per route (the register's invariant), cell
// narrowings service-side (D-040 two-layer). NO new code (D-323's numbers
// paragraph). The note and tag writes ride `sales.manage_customers` under §7
// narrowing 22: sales on assigned customers, facility_admin and group_admin,
// and finance, which holds the code for its credit cell alone, is refused
// 403. The redaction rides the same code under narrowing 18, the admin
// lineage the archive keys on, as spec 09 §12's note-redaction question was
// answered on 2026-10-03 (D-323 ruling 3). The tag list is curated under `sales.tier.manage` (group_admin, facility_admin). The
// reads ride `sales.read` under the customer book's lineages, so qc and the
// storekeeper, who hold the code for the document books, are refused 403.
//
// ORDERING. Refusals are listed in the order each door meets them (spec 09
// §5). The route guard's 403 for a missing code comes first and is not
// listed. On every `{id}` route the path id is parsed next, so a malformed id
// answers 400 before any refusal a row lists, and a malformed body answers
// 400 before the refusals a row lists unless the row places it.
//
// IDEMPOTENCY AND NOTE TEXT. Every mutation requires `Idempotency-Key`, and
// D-303 serves a replay only to the principal who made the request. The note
// append, pin, unpin and redact store their answer without `body`, so the
// idempotency store keeps no note text and a replay carries none; the client
// reads the timeline again (spec 09 §2.26). The restore stores its answer
// without `notes` and `pinned_note`, and the merge's answer nests no
// `Customer` at all, because `@IdempotentRedact` removes top-level keys alone.
//
// EVENTS. Notes, tags, attachments, the duplicate check and the restore emit
// no event (D-323 ruling 15, D-324 ruling 12, the D-313 precedent), because their
// consumers 13 and 14 are absent. The merge emits `sales.customer_merged`,
// the catalog's twenty-seventh producer name, and also
// `sales.customer_tier_changed` and `sales.customer_credit_changed` when it
// changes the winner's tier or credit, through the helper the PATCH calls
// (spec 09 §3.13). Audit rows carry ids, kinds, counts and flags and never a
// note's words, a tag's name or a file's title (spec 09 §7's E2-2 payload
// table).

const sessionSecurity = [{ sessionCookie: [] }];
const idempotencyHeader = { $ref: "#/components/parameters/IdempotencyKey" };

function route(config: {
  method: "get" | "post" | "patch" | "delete";
  path: string;
  operationId: string;
  summary: string;
  permission: string;
  request?: RouteConfig["request"];
  responses: RouteConfig["responses"];
  description: string;
  /** A POST that writes nothing and takes no Idempotency-Key (route 14, the
   * duplicate check, D-333 ruling 1). Its server handler is
   * `@IdempotencyExempt()`, and the route manifest says so. */
  idempotencyExempt?: boolean;
}): void {
  const mutation = config.method !== "get" && config.idempotencyExempt !== true;
  registry.registerPath({
    method: config.method,
    path: config.path,
    operationId: config.operationId,
    tags: ["sales"],
    summary: config.summary,
    description: `${config.description} Requires \`${config.permission}\`.`,
    security: sessionSecurity,
    ...(config.request !== undefined ? { request: config.request } : {}),
    ...(mutation ? { parameters: [idempotencyHeader] } : {}),
    responses: { ...defaultErrorResponses(), ...config.responses },
  });
}

const jsonBody = (schema: z.ZodType) => ({
  body: { content: { "application/json": { schema } } },
});
const ok = (description: string, schema: z.ZodType) => ({
  200: { description, content: { "application/json": { schema } } },
});
const created = (description: string, schema: z.ZodType) => ({
  201: { description, content: { "application/json": { schema } } },
});

const IdParam = z.object({ id: z.guid() });

/** Cursor pagination (§5): default 50, max 200. */
const cursorQuery = {
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
};

const READ_LINEAGE_403 =
  "PERMISSION_DENIED: with no grant named, for a caller whose authorising lineage the customer book refuses (qc, storekeeper and any lineage other than group_admin, facility_admin, sales, finance and planner)";

const WRITE_LINEAGE_403 =
  "PERMISSION_DENIED: with no grant named, for a caller outside §7 narrowing 22's writers (sales on assigned customers, facility_admin, group_admin). finance holds `sales.manage_customers` for its credit cell alone and is refused here";

/** Every door that answers 422 `CUSTOMER_ARCHIVED` says this since E2-2b
 * (spec 09 §5's E2-2 row 22). */
const MERGED_EXTENSION =
  "`CUSTOMER_ARCHIVED` carries `extensions.merged_into_customer_id` naming the winner when the customer was merged (spec 09 §5's E2-2 row 22)";

const NARROWING_22 =
  "Narrowed by §7 narrowing 22: sales on assigned customers, facility_admin and group_admin write a customer's notes and tags, and finance is refused 403. The door first holds the customer FOR KEY SHARE (`holdCustomerForWrite`), and for a sales principal a customer it is not assigned answers 404 `CUSTOMER_NOT_FOUND` before any archived refusal.";

// ===========================================================================
// notes (spec 09 §2.26; §5's E2-2 rows 1 to 3a)
// ===========================================================================

route({
  method: "get",
  path: "/v1/customers/{id}/notes",
  operationId: "listSalesCustomerNotes",
  summary: "The customer's notes timeline (pinned first, then newest)",
  permission: "sales.read",
  description:
    "Spec 09 §5's E2-2 row 1. The pinned note first and then the newest, cursor keyset on `(pinned, created_at, id)`, so the pinned note heads the first page. Every reader of the customer is served the bodies, as `notes` has always been served (spec 09 §7, D-323 ruling 3), and a redacted note is served with its marker body and `redacted_by_name`. A legacy note, copied from the old field by migration 0077 or 0078, has no author. The caller's customer door decides the customer: a sales principal reads its assigned customers alone.",
  request: { params: IdParam, query: z.object({ ...cursorQuery }) },
  responses: {
    ...ok("The notes, the pinned note first and then the newest", CustomerNoteListResponse),
    403: problemResponse(READ_LINEAGE_403),
    404: problemResponse(
      "CUSTOMER_NOT_FOUND (cross-tenant ids, and a customer the caller's door refuses, are invisible)",
    ),
  },
});

route({
  method: "post",
  path: "/v1/customers/{id}/notes",
  operationId: "createSalesCustomerNote",
  summary: "Append a note to a customer (optionally pinning it)",
  permission: "sales.manage_customers",
  description: `Spec 09 §5's E2-2 row 2. Appends a typed note under the caller's name. \`pin: true\` pins it and unpins the pinned note in the same transaction, under the customer's pin lock, so two concurrent pins both answer and the later writer's note ends pinned (spec 09 §2.26). Audited \`sales_customer_note.appended\` with \`replaced_note_id\` when it unpinned another. ${NARROWING_22} An archived customer takes no new note. The answer is stored for replay without \`body\`.`,
  request: { params: IdParam, ...jsonBody(CustomerNoteCreateRequest) },
  responses: {
    ...created("The appended note", CustomerNoteWriteResponse),
    403: problemResponse(WRITE_LINEAGE_403),
    404: problemResponse("CUSTOMER_NOT_FOUND"),
    422: problemResponse(
      `CUSTOMER_ARCHIVED: an archived customer takes no new note (spec 09 §3.1 item 4). ${MERGED_EXTENSION}`,
    ),
  },
});

const NOTE_LOOP =
  "The door reads the note, holds the customer it sits on FOR KEY SHARE and re-reads the note under that hold, following a note a merge moved to the customer it now sits on (spec 09 §3.1 item 4). For a sales principal a note on a customer it is not assigned answers 404 `NOTE_NOT_FOUND`, as a missing note does.";

route({
  method: "post",
  path: "/v1/customer-notes/{id}/pin",
  operationId: "pinSalesCustomerNote",
  summary: "Pin a note (unpinning the customer's pinned note)",
  permission: "sales.manage_customers",
  description: `Spec 09 §5's E2-2 row 3. Pins the note and unpins the customer's pinned note in the same transaction, under the customer's pin lock, audited \`sales_customer_note.pinned\` with \`replaced_note_id\` when it unpinned another. Pinning the pinned note answers 200 with the note, writes nothing and appends no audit row. ${NOTE_LOOP} §7 narrowing 22 applies as on the append. The answer is stored for replay without \`body\`.`,
  request: { params: IdParam },
  responses: {
    ...ok("The note, pinned", CustomerNoteWriteResponse),
    403: problemResponse(WRITE_LINEAGE_403),
    404: problemResponse("NOTE_NOT_FOUND"),
    422: problemResponse(`CUSTOMER_ARCHIVED: the note's customer is archived. ${MERGED_EXTENSION}`),
  },
});

route({
  method: "post",
  path: "/v1/customer-notes/{id}/unpin",
  operationId: "unpinSalesCustomerNote",
  summary: "Unpin a note",
  permission: "sales.manage_customers",
  description: `Spec 09 §5's E2-2 row 3. Unpins the note and keeps it on the timeline, audited \`sales_customer_note.unpinned\`. Unpinning a note that is not pinned answers 200 with the note, writes nothing and appends no audit row. This is how a redacted pinned note is unpinned, because PATCH \`notes: null\` resends the value the alias already serves and writes nothing. ${NOTE_LOOP} §7 narrowing 22 applies as on the append. The answer is stored for replay without \`body\`.`,
  request: { params: IdParam },
  responses: {
    ...ok("The note, unpinned", CustomerNoteWriteResponse),
    403: problemResponse(WRITE_LINEAGE_403),
    404: problemResponse("NOTE_NOT_FOUND"),
    422: problemResponse(`CUSTOMER_ARCHIVED: the note's customer is archived. ${MERGED_EXTENSION}`),
  },
});

route({
  method: "post",
  path: "/v1/customer-notes/{id}/redact",
  operationId: "redactSalesCustomerNote",
  summary: "Redact a note's words (admins)",
  permission: "sales.manage_customers",
  description:
    "Spec 09 §5's E2-2 row 3a, as answered to spec 09 §12's note-redaction question (D-323 ruling 3). Only a caller on the group_admin or facility_admin lineage may press it, the rule the archive keys on (§7 narrowing 18). The body becomes the fixed marker `[redacted]` and `redacted_at` and the redactor are stamped, keeping the author, the time and the pin; audited `sales_customer_note.redacted` with the removed body's length and none of its words. Redacting a `legacy` note also sets the customer's old `notes` column to NULL in the same transaction, taking the customer FOR UPDATE first (spec 09 §2.26). A redaction erases words and adds no business, so an archived customer's note is redacted without a restore and the door takes no archived refusal. A redacted note answers 200 and writes nothing. Both paths follow a note a merge moved. The answer is stored for replay without `body`.",
  request: { params: IdParam },
  responses: {
    ...ok("The note, redacted", CustomerNoteWriteResponse),
    403: problemResponse(
      "PERMISSION_DENIED: with no grant named, for a caller outside the group_admin and facility_admin lineages (§7 narrowing 18)",
    ),
    404: problemResponse("NOTE_NOT_FOUND"),
  },
});

// ===========================================================================
// tags (spec 09 §2.27, §2.28; §5's E2-2 rows 4 to 9)
// ===========================================================================

route({
  method: "get",
  path: "/v1/sales-tags",
  operationId: "listSalesTags",
  summary: "The tenant's tags (active first, then archived as asked)",
  permission: "sales.read",
  description:
    "Spec 09 §5's E2-2 row 4. The active tags first and then, as `archived` asks, the archived tags an active customer carries (`in_use`) or every archived tag (`all`); `none` is the default. Each part is in keyset order on the lower-cased name and the id. Archived tags are never deleted and every harness run adds some, so the read is paged and no answer is unbounded. The Tag filters read `archived=in_use` at `limit=200` and read on while `next_cursor` is set, and §4.13's Show archived reads `all` a page at a time. No agent tool serves a tag name (D-323 ruling 5).",
  request: {
    query: z.object({
      archived: SalesTagArchivedFilter.optional(),
      ...cursorQuery,
    }),
  },
  responses: {
    ...ok("The tags, active first", SalesTagListResponse),
    403: problemResponse(READ_LINEAGE_403),
  },
});

route({
  method: "post",
  path: "/v1/sales-tags",
  operationId: "createSalesTag",
  summary: "Create a tag",
  permission: "sales.tier.manage",
  description:
    "Spec 09 §5's E2-2 row 5. The name is stored trimmed, and a name holding `;` is the body's 400. The create takes the tenant's tag lock, then judges the active-name unique and the cap of 200 active tags under it, so two creates at the cap queue and the second answers 409 `TAG_LIMIT_REACHED` (spec 09 §2.27). Audited `sales_tag.created`.",
  request: jsonBody(SalesTagCreateRequest),
  responses: {
    ...created("The created tag", SalesTag),
    409: problemResponse(
      "TAG_NAME_TAKEN: an active tag already has this name without regard to case; then TAG_LIMIT_REACHED: the tenant holds 200 active tags",
    ),
  },
});

route({
  method: "patch",
  path: "/v1/sales-tags/{id}",
  operationId: "updateSalesTag",
  summary: "Rename a tag",
  permission: "sales.tier.manage",
  description:
    "Spec 09 §5's E2-2 row 6. The name is stored trimmed, a name holding `;` is the body's 400, and the rename takes the create's tag lock before it reads the names, so a rename racing a create or another rename to one name queues and the later one answers 409 (spec 09 §2.27). Audited `sales_tag.updated` naming the fields that changed.",
  request: { params: IdParam, ...jsonBody(SalesTagUpdateRequest) },
  responses: {
    ...ok("The renamed tag", SalesTag),
    404: problemResponse("TAG_NOT_FOUND"),
    409: problemResponse(
      "TAG_NAME_TAKEN: another active tag already has this name without regard to case",
    ),
  },
});

route({
  method: "post",
  path: "/v1/sales-tags/{id}/archive",
  operationId: "archiveSalesTag",
  summary: "Archive a tag",
  permission: "sales.tier.manage",
  description:
    "Spec 09 §5's E2-2 row 7. Archiving hides the tag from every picker and keeps its links, so an archived tag still shows on the customers that carry it and still filters. No route deletes a tag. An archived tag answers 200 again and writes nothing. Audited `sales_tag.archived`.",
  request: { params: IdParam },
  responses: {
    ...ok("The archived tag", SalesTag),
    404: problemResponse("TAG_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/customers/{id}/tags",
  operationId: "attachSalesCustomerTag",
  summary: "Tag a customer",
  permission: "sales.manage_customers",
  description: `Spec 09 §5's E2-2 row 8. Links the tag to the customer and answers every tag the customer carries, audited \`sales_customer_tag.attached\`. The insert is \`ON CONFLICT (tenant_id, customer_id, tag_id) DO NOTHING\`, so an existing link, and the loser of two concurrent attaches, answers 200, writes nothing and appends no audit row (spec 09 §2.28). ${NARROWING_22}`,
  request: { params: IdParam, ...jsonBody(CustomerTagAttachRequest) },
  responses: {
    ...ok("Every tag the customer carries after the write", CustomerTagsResponse),
    403: problemResponse(WRITE_LINEAGE_403),
    404: problemResponse("CUSTOMER_NOT_FOUND; then, for the body's tag, TAG_NOT_FOUND"),
    409: problemResponse("TAG_ARCHIVED: an archived tag is never newly attached"),
    422: problemResponse(
      `CUSTOMER_ARCHIVED: an archived customer takes no new tag. ${MERGED_EXTENSION}`,
    ),
  },
});

route({
  method: "delete",
  path: "/v1/customers/{id}/tags/{tagId}",
  operationId: "detachSalesCustomerTag",
  summary: "Untag a customer",
  permission: "sales.manage_customers",
  description: `Spec 09 §5's E2-2 row 9. Deletes the link, because a link is a routing fact with no history of its own, and answers every tag the customer carries, audited \`sales_customer_tag.detached\`. A missing link answers 200 and writes nothing. Untagging holds the customer as every tag writer does, so an archived customer answers 422 (D-323 ruling 4). ${NARROWING_22}`,
  request: { params: z.object({ id: z.guid(), tagId: z.guid() }) },
  responses: {
    ...ok("Every tag the customer carries after the write", CustomerTagsResponse),
    403: problemResponse(WRITE_LINEAGE_403),
    404: problemResponse("CUSTOMER_NOT_FOUND"),
    422: problemResponse(
      `CUSTOMER_ARCHIVED: an archived customer's tags are not changed. ${MERGED_EXTENSION}`,
    ),
  },
});

// ===========================================================================
// sales attachments (spec 09 §2.29; §5's E2-2 rows 10 to 13; §7 narrowings
// 19, 21 and 22; D-323 rulings 8 to 12, D-335), P3-E2 PR E2-2a2
//
// WHO. Every door's route guard is `sales.read`, because the write code
// depends on the subject the body or the stored link names, so one code per
// route cannot carry it. The service then applies the subject's own doors: to
// read, the subject's read door (the customer door, the quote door or the
// order door, so qc and the storekeeper read the quotes and orders they
// read), and to write, the subject's write code and its narrowing,
// `sales.manage_customers` under narrowing 22 for a customer,
// `sales.quote.write` for a quote and `sales.order.write` for an order
// (narrowing 21). A `commercial` file reaches a holder of `sales.read_value`
// alone, and any other reader gets `withheld_count` from the list and 404
// `ATTACHMENT_NOT_FOUND` from the download and the detach (narrowing 19).
// ===========================================================================

const SUBJECT_404 =
  "CUSTOMER_NOT_FOUND, QUOTE_NOT_FOUND or ORDER_NOT_FOUND for the subject: another tenant's, or one the caller's door does not answer, such as a customer a sales principal is not assigned";

const ATTACHMENT_404 =
  "ATTACHMENT_NOT_FOUND: no such attachment, another tenant's, one whose subject the caller's read door does not answer, or a `commercial` attachment read by a caller without `sales.read_value` (§7 narrowing 19), each indistinguishable from the others";

route({
  method: "post",
  path: "/v1/sales-attachments",
  operationId: "createSalesAttachment",
  summary: "Attach a stored registry file to a customer, a quote or an order",
  permission: "sales.read",
  description: `Spec 09 §5's E2-2 row 10 and §2.29 (D-323 rulings 8 to 11). The file was uploaded first through the registry's three steps under the purpose \`sales_attachment\`, and \`POST /v1/files/{id}/store\` checked its bytes against the declared type, so a file that says it is something else was refused there with \`FILE_INTEGRITY_MISMATCH\` and never reaches this door as stored. The body names the file, exactly one subject, the category and an optional title. The service checks the subject's write code and narrowing, reads the subject, holds the customer the subject is or its document names FOR KEY SHARE (\`holdCustomerForWrite\`) and re-reads a quote or an order under that hold, so a document a merge moved is judged on the customer it now names and an attach meets a merge on the customer row before its insert (§3.1 item 4, §3.13). It then checks the file and writes the link, audited \`sales_attachment.attached\` with the file id, the subject and the category and never the title. One file has one link for good, active or detached, so a second attach of it, and the loser of two concurrent attaches, answers 409 \`ATTACHMENT_ALREADY_LINKED\`. Emits no domain event (D-323 ruling 15). No scan runs in E2-2a2 (D-335 ruling 3). The answer is stored for replay without \`title\`, so a replay carries no title (D-339).`,
  request: jsonBody(SalesAttachmentCreateRequest),
  responses: {
    ...created("The attachment", SalesAttachmentWriteResponse),
    400: problemResponse(
      "VALIDATION_FAILED: a malformed body, or one that names no subject or more than one of `customer_id`, `quote_id` and `sales_order_id` (`attachment_one_subject`), answered before anything is read",
    ),
    403: problemResponse(
      "PERMISSION_DENIED, the service's check of the subject's write code and its narrowing: `missing_permission` naming `sales.manage_customers` for a customer, `sales.quote.write` for a quote or `sales.order.write` for an order when the caller lacks it, and no grant named for a caller outside the subject's write lineage (finance on a customer, §7 narrowing 22); then, after the file checks, ATTACHMENT_FILE_NOT_YOURS: another member uploaded the file",
    ),
    404: problemResponse(
      `${SUBJECT_404}; then, after the archived refusal, NOT_FOUND for the file, the registry's shared answer for a missing file and another tenant's`,
    ),
    409: problemResponse(
      "In this order: FILE_NOT_STORED, the registry's own code for a file that is not `stored` (an upload not yet verified, or one whose bytes failed the check); ATTACHMENT_FILE_WRONG_PURPOSE, the file was uploaded under another purpose than `sales_attachment`; then, after ATTACHMENT_FILE_NOT_YOURS, ATTACHMENT_ALREADY_LINKED, the file already has a link, active or detached (`sales_attachments_file_idx`)",
    ),
    422: problemResponse(
      `CUSTOMER_ARCHIVED: the customer the subject is, or the customer its quote or order names, is archived, read under the hold (spec 09 §3.1 item 4). ${MERGED_EXTENSION}`,
    ),
  },
});

route({
  method: "get",
  path: "/v1/sales-attachments",
  operationId: "listSalesAttachments",
  summary: "A customer's, a quote's or an order's attachments",
  permission: "sales.read",
  description:
    "Spec 09 §5's E2-2 row 11 and §7 narrowing 19 (D-335 ruling 1). Exactly one of `customer_id`, `quote_id` and `sales_order_id` names the subject, and the subject's read door decides whether the caller reads it. Active attachments, newest first, cursor keyset on `(attached_at, id)`, and detached ones as well with `include_detached=true`. A customer lists its own attachments, never those of its quotes and orders, which their own panels list. A `technical` file is listed to every reader of the subject. A `commercial` file is listed only to a holder of `sales.read_value`, and `withheld_count` tells any other reader how many it was not shown, counted over the whole subject and never one page of it. The Documents tab reads the customer's quotes and orders through `GET /v1/quotes?customer_id=` and `GET /v1/orders?customer_id=`.",
  request: {
    query: z.object({
      customer_id: z.guid().optional(),
      quote_id: z.guid().optional(),
      sales_order_id: z.guid().optional(),
      include_detached: z.enum(["true", "false"]).optional(),
      ...cursorQuery,
    }),
  },
  responses: {
    ...ok(
      "The attachments the caller may read, newest first, and the count of commercial ones it is not shown",
      SalesAttachmentListResponse,
    ),
    400: problemResponse(
      "VALIDATION_FAILED: a malformed query, or one that names no subject or more than one of `customer_id`, `quote_id` and `sales_order_id` (`attachment_one_subject`)",
    ),
    403: problemResponse(
      "PERMISSION_DENIED with no grant named, the subject's read door's own lineage refusal: a customer subject refuses the lineages the customer book refuses (qc, the storekeeper), as `GET /v1/customers/{id}` does",
    ),
    404: problemResponse(SUBJECT_404),
  },
});

route({
  method: "get",
  path: "/v1/sales-attachments/{id}/download",
  operationId: "getSalesAttachmentDownload",
  summary: "A five-minute download URL for an attachment",
  permission: "sales.read",
  description:
    "Spec 09 §5's E2-2 row 12 and §2.29. The door reads the attachment, applies row 11's visibility to its subject and its category, and only then mints the registry's presigned GET through `resolveInternal`, for five minutes, with `attachment` disposition and the name built from the verified type, so a file whose bytes are a PDF downloads with a `.pdf` name whatever its uploaded name said (§9 item 39). A detached attachment still downloads for a reader who may see it, because detaching keeps the file. `GET /v1/files/{id}` refuses this purpose (`publicResolve: false`), so this route is the file's only door.",
  request: { params: IdParam },
  responses: {
    ...ok("The download URL and when it expires", SalesAttachmentDownloadResponse),
    404: problemResponse(ATTACHMENT_404),
  },
});

route({
  method: "post",
  path: "/v1/sales-attachments/{id}/detach",
  operationId: "detachSalesAttachment",
  summary: "Detach an attachment and keep its file",
  permission: "sales.read",
  description:
    "Spec 09 §5's E2-2 row 13 and §2.29. The door applies row 11's visibility first, because the subject is known only once the attachment is read, so a withheld file answers 404 even to a caller with no write code. It then applies row 10's write rule for the attachment's subject, reads the attachment FOR UPDATE (§3.1 item 4), sets it `detached` with the detach stamps and keeps both the link row and the file, audited `sales_attachment.detached`. A detached attachment answers 200 again and writes nothing. No body is read. Emits no domain event. The answer is stored for replay without `title`, so a replay carries no title (D-339).",
  request: { params: IdParam },
  responses: {
    ...ok("The attachment, detached", SalesAttachmentWriteResponse),
    403: problemResponse(
      "PERMISSION_DENIED, after the 404: the caller lacks the subject's write code (`missing_permission` naming `sales.manage_customers`, `sales.quote.write` or `sales.order.write`) or falls outside its narrowing (finance on a customer, §7 narrowing 22)",
    ),
    404: problemResponse(ATTACHMENT_404),
  },
});

// ===========================================================================
// the duplicate check (spec 09 §3.13; §5's E2-2 row 14; §7 narrowing 20;
// D-324 rulings 1 to 3), P3-E2 PR E2-2c
// ===========================================================================

route({
  method: "post",
  path: "/v1/customers/check-duplicates",
  operationId: "checkSalesCustomerDuplicates",
  summary: "Name existing customers a new one may duplicate (a warning, never a block)",
  permission: "sales.manage_customers",
  description:
    "Spec 09 §5's E2-2 row 14 and §3.13. A dry run the New customer form calls after a pause in typing: it writes no customer, and the create answers 201 whatever it finds, naming `POSSIBLE_DUPLICATE` in its `X-Sales-Warning` list (§5's E2-2 row 18). It first looks for an ACTIVE customer with the same `tax_id`, then for customers whose `display_name` or `legal_name` is similar through the trigram operator, re-scored after the legal-form words of every market are removed (`packages/domain-kernel/src/company-name/legal-forms.ts`, D-324 ruling 1) and kept at a provisional similarity floor that D-324's census of staging's customer names fixes (D-332 ruling 4). Archived customers are never candidates. At most five candidates, the exact tax-id match first. §7 narrowing 20: the sales and admin lineages (sales, facility_admin, group_admin) may press it, and finance, which holds the code for its credit cell alone, is refused 403. A candidate is a customer the caller's customer door answers, and its `legal_name` is served only as far as `LegalNameReach` serves it. For a caller the door narrows to assigned customers, `hidden_count` counts only display-name matches on customers it cannot open, and a legal-name or tax-id match on such a customer gives no signal, because either would tell a representative that a given company is the factory's customer. A body naming no name and no tax id answers no candidates. Throttled per principal at 30 calls a minute through identity's Postgres fixed-window `ThrottleService` under the kind `customer_dup_check`, keyed on `{tenant_id}:{user_id}`, so the bound holds across API instances and deploys. It takes no `Idempotency-Key` and stores no answer (D-333 ruling 1): it writes nothing a replay could double, and the idempotency store never deletes a row, so storing each check would keep candidates' names for good. A key the client sends is ignored. `country` is accepted, because the create form sends it, and is not read: the tax-id match is exact on the stored value, and names are compared whatever the country. `hidden_count` is exact up to the 100 display-name matches the check reads before it ranks.",
  idempotencyExempt: true,
  request: jsonBody(CustomerDuplicateCheckRequest),
  responses: {
    ...ok(
      "At most five candidates, the exact tax-id match first, and the count of display-name matches the caller cannot open",
      CustomerDuplicateCheckResponse,
    ),
    403: problemResponse(
      "PERMISSION_DENIED: with no grant named, for a caller outside the sales and admin lineages (sales, facility_admin, group_admin). finance holds `sales.manage_customers` for its credit cell alone and is refused here (§7 narrowing 20)",
    ),
    429: problemResponse(
      "TOO_MANY_ATTEMPTS: more than 30 checks in the principal's current minute (kind `customer_dup_check`). Carries `Retry-After`, the seconds until the window turns",
    ),
  },
});

// ===========================================================================
// the merge and the restore (spec 09 §2.30, §2.33, §3.13, §3.14, §4.17; §5's
// E2-2 rows 15 to 17; §7 narrowings 18 and 19; D-324 rulings 4 to 16),
// P3-E2 PR E2-2b
//
// WHO. All three are admin acts under `sales.manage_customers`, keyed on the
// admin lineage exactly as the archive is (`hasLineage(ctx, ADMIN_LINEAGES)`,
// §7 narrowing 18). A tenant's clone of group_admin or facility_admin may
// press them, a clone of sales may not (D-224), and finance, which holds the
// code for its credit cell, is refused 403. No new permission code.
//
// THE PATH CUSTOMER IS THE WINNER on the preview and the merge. A merged
// customer is archived with `merged_into_customer_id` set, and its 360 still
// answers as the tombstone (row 20).
// ===========================================================================

const ADMIN_LINEAGE_403 =
  "PERMISSION_DENIED: with no grant named, for a caller outside the group_admin and facility_admin lineages (§7 narrowing 18). finance holds `sales.manage_customers` for its credit cell alone and is refused here, and so is a tenant's clone of sales";

route({
  method: "post",
  path: "/v1/customers/{id}/merge-preview",
  operationId: "previewSalesCustomerMerge",
  summary: "Preview merging another customer into this one (a dry run)",
  permission: "sales.manage_customers",
  description:
    "Spec 09 §5's E2-2 row 15, §3.13 and §4.17. The path customer is the winner and `loser_id` names the other. A dry run that writes nothing and answers both customers' profile values, each row's version (`xmin` as text, D-318's instrument), a count per table of what would move to the winner and what would stay on the loser, the users who would receive a collaborator row on the winner (`gains_access`), the commercial attachments whose audience would change, and the blockers the merge would refuse on (`MERGE_TAX_ID_CONFLICT`, `MERGE_CURRENCY_MISMATCH`). The legal name follows `LegalNameReach` and `credit_limit_minor` is served only where the customer card serves it (`customerVisibility(...).creditVisible`), so a facility_admin clone that holds `sales.read_value` is served none. The commercial attachments are named by id and title only to a holder of `sales.read_value`, and any other caller is served `commercial_audience_withheld_count` in their place (§7 narrowing 19). Distributor products are counted against the winner's current tier, the choice the screen starts on. An archived customer is previewed, and the merge then refuses it 409 `MERGE_CUSTOMER_ARCHIVED`. It takes no `Idempotency-Key` and stores no answer (D-334 ruling 4, the reason of D-333 ruling 1): it writes nothing a replay could double, and the idempotency store never deletes a row, so storing each preview would keep both customers' legal names, tax ids and limits for good. A key the client sends is ignored.",
  idempotencyExempt: true,
  request: { params: IdParam, ...jsonBody(CustomerMergePreviewRequest) },
  responses: {
    ...ok("The preview", CustomerMergePreview),
    403: problemResponse(ADMIN_LINEAGE_403),
    404: problemResponse(
      "CUSTOMER_NOT_FOUND, for the path customer or the loser (cross-tenant ids are invisible)",
    ),
    422: problemResponse("MERGE_SAME_CUSTOMER: `loser_id` names the path customer"),
  },
});

route({
  method: "post",
  path: "/v1/customers/{id}/merge",
  operationId: "mergeSalesCustomer",
  summary: "Merge the named loser into this customer, in one transaction",
  permission: "sales.manage_customers",
  description:
    "Spec 09 §5's E2-2 row 16 and §3.13 (D-324 rulings 4 to 11, D-334 ruling 1). The path customer is the winner. One transaction refuses an archived customer on its plain reads, before any lock (D-334 ruling 8), takes both customers FOR UPDATE in id order and, under those locks, refuses a customer archived since, compares each row's `xmin` with the version the body sends, recomputes `gains_access` and the commercial attachment set and compares them with the body, applies the two field checks and the tax-id and currency refusals, then takes the loser's movable rows FOR UPDATE NOWAIT and writes. It writes the winner's chosen profile values, moves the loser's contacts with their channels (a moved contact is never primary), notes (the winner's pinned note stays pinned and the loser's moves unpinned when both have one), quotes, orders, deals and tasks, open or finished, and pending notifications (finished ones stay on the loser and are counted in `left_on_loser`, D-334 ruling 6), the overrides and distributor products the winner can hold, its own attachments, active or detached (a quote's or an order's attachment follows its document), and module 17's conversations, copies tag links the winner lacks, gives each user in `add_collaborators` a collaborator row on the winner through the assignments writer, and archives the loser with `merged_into_customer_id`, `merged_at` and its `customers.notes` cleared. A second merge that takes a winner away re-points every older tombstone at the new winner, so the pointer is always one hop. Every re-parented or copied row is recorded in `customer_merge_moves`, and the merge in `customer_merges` with the field choices and the winner's overwritten values, which no route serves. Audited `sales.customer_merged` on both customers with `merge_id`. Emits `sales.customer_merged`, and `sales.customer_tier_changed` or `sales.customer_credit_changed` when the winner's tier or credit changed, with the payloads a PATCH sends. `add_collaborators` and `skip_collaborators` together name exactly the preview's `gains_access`, and a user in both, or twice in one, is the body's 400. `commercial_attachment_ids` names exactly the preview's `commercial_audience_changes`; a caller without `sales.read_value` sends the empty list and `commercial_attachment_count`, the withheld count its preview read, and an absent count reads as 0. The answer names the winner and nests no `Customer`, because the idempotency store keeps a nested object as sent (spec 09 §2.26); the client reads the winner's 360 again.",
  request: { params: IdParam, ...jsonBody(CustomerMergeRequest) },
  responses: {
    ...ok(
      "The merge's id, the winner's id and the counts of what moved and what stayed",
      CustomerMergeResult,
    ),
    400: problemResponse(
      "VALIDATION_FAILED: a malformed body, a user named in both `add_collaborators` and `skip_collaborators` (`merge_collaborator_in_both_lists`), or a user named twice in one of them (`merge_collaborator_repeated`, D-334 ruling 7), each judged case-blind; then, under the locks after the version check, on `fields.credit_limit_minor` with the reason `merge_credit_currency_split` when the default currencies differ and `fields` takes the currency and the limit from different customers, and on `tax_id` with the reason `tax_id_format` when the final country and tax id fail the country's rule",
    ),
    403: problemResponse(
      `${ADMIN_LINEAGE_403}; then \`missing_permission: sales.tier.manage\` when \`fields\` takes a tier that differs from the winner's and the caller lacks that code, checked after MERGE_SAME_CUSTOMER and before the locks (the PATCH's two-layer rule)`,
    ),
    404: problemResponse(
      "CUSTOMER_NOT_FOUND, for the path customer or the loser (cross-tenant ids are invisible)",
    ),
    409: problemResponse(
      "In this order: MERGE_CUSTOMER_ARCHIVED, either customer is archived, answered on the plain reads before any lock, so a merge never locks a tombstone as a participant, and again under both customers' locks for one archived since (D-334 ruling 8); then under the locks MERGE_STALE, a customer's version moved since the preview, or `gains_access` or the commercial attachment set the merge recomputes differs from the body (by ids for a holder of `sales.read_value`, by `commercial_attachment_count` for any other caller), and nothing is written; then, after the two 400 field checks, MERGE_TAX_ID_CONFLICT, both tax ids are set and differ; MERGE_CURRENCY_MISMATCH, the default currencies differ and the loser holds any quote, order or deal, open or finished; MERGE_IMPORT_ORDER_REF_CONFLICT, the loser and the winner each hold an imported order that is not cancelled with the same `import_order_ref`, with `extensions.conflicts` naming each reference and the two orders' numbers, also answered when an import commit lands such an order during the move, and nothing is written (D-353 ruling 5)",
    ),
    422: problemResponse("MERGE_SAME_CUSTOMER: `loser_id` names the path customer"),
    503: problemResponse(
      "RESOURCE_BUSY with `Retry-After: 2`: another transaction holds one of the loser's movable rows (an acceptance holding its quote, a deal move, the dispatcher's claimed notification), taken FOR UPDATE NOWAIT so the rare admin act is the one that yields. Nothing was written, and the same body can be sent again",
    ),
  },
});

route({
  method: "post",
  path: "/v1/customers/{id}/restore",
  operationId: "restoreSalesCustomer",
  summary: "Restore an archived customer (never a merged one)",
  permission: "sales.manage_customers",
  description:
    "Spec 09 §5's E2-2 row 17 and §3.14 (D-324 ruling 14). Takes the customer FOR UPDATE and returns an archived customer to `active`, audited `sales.customer_restored` with its prior status. An active customer answers 200 and writes nothing. A merged customer answers 409 `CUSTOMER_MERGED`, because reviving it would give one company two live records again. What the archive and D-317 ended stays ended: a contact archived earlier stays archived with its channels gone, a system task the sweep closed stays closed, and a notification that ended `skipped_customer_archived` stays so. Emits no domain event. The answer is stored for replay without `notes` and `pinned_note` (spec 09 §2.26).",
  request: { params: IdParam },
  responses: {
    ...ok("The customer, active", Customer),
    403: problemResponse(ADMIN_LINEAGE_403),
    404: problemResponse("CUSTOMER_NOT_FOUND"),
    409: problemResponse(
      "CUSTOMER_MERGED: the customer was merged into another; `extensions.merged_into_customer_id` names the winner",
    ),
  },
});
